import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { toast } from "@gigpro/ui/Toast";
import type { JobsSubscriptionStatus } from "@sidegigllc/api/client";
import { AddPaymentMethodNote } from "domains/hire/components/AddPaymentMethodNote";
import { useResubscribeJobsPlan } from "domains/hire/hooks/useResubscribeJobsPlan";
import { JOBS_PLAN_AMOUNT, JOBS_PLAN_PRICE, jobsNeedsPaymentMethod, jobsPaymentLabel } from "domains/hire/jobsPlan";
import { toastApiError } from "utils/toastApiError";

type ResubscribeDialogProps = {
  open: boolean;
  onClose: () => void;
  locationId: number;
  locationName: string;
  status: JobsSubscriptionStatus;
  /** What to do once the plan is active again, e.g. re-submit the job that was refused. */
  onStarted?: () => Promise<unknown>;
};

/** Resubscribing is charged right away, so it always asks first and names what is charged. */
export const ResubscribeDialog = ({
  open,
  onClose,
  locationId,
  locationName,
  status,
  onStarted,
}: ResubscribeDialogProps) => {
  const resubscribe = useResubscribeJobsPlan(locationId);
  const payment = jobsPaymentLabel(status);
  const needsMethod = jobsNeedsPaymentMethod(status);

  const handleResubscribe = async () => {
    try {
      await resubscribe.mutateAsync();
      toast.success("Your Jobs plan is active again.");
      onClose();
      await onStarted?.();
    } catch (error) {
      toastApiError(error);
    }
  };

  const paymentLine = () => {
    if (status.collection === "INVOICE") return "Goes on your monthly Qwick invoice from today. No free trial.";
    if (!needsMethod) return `We'll charge ${JOBS_PLAN_AMOUNT} to ${payment} today, then every month. No free trial.`;
    return <AddPaymentMethodNote locationId={locationId} />;
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle description={`${JOBS_PLAN_PRICE} at ${locationName}`}>Resubscribe to your Jobs plan</DialogTitle>
      <div className="space-y-3 pt-2">
        <p>
          {status.state === "ARCHIVED"
            ? "You'll see all your past applicants again right away. Jobs that closed stay closed, so repost the ones you still need."
            : "You'll see all your applicants again right away, and your jobs stay open."}
        </p>
        <p className="rounded-sm bg-background p-3 text-small">{paymentLine()}</p>
      </div>
      <DialogFooter className="mt-6 grid grid-cols-2 gap-2">
        <Button variant="text" onClick={onClose}>
          Not now
        </Button>
        <Button
          variant="contained"
          onClick={handleResubscribe}
          loading={resubscribe.isPending}
          disabled={needsMethod || resubscribe.isPending}
        >
          Resubscribe for {JOBS_PLAN_PRICE}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
